'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const DEFAULT_CASH_FLOWS = '3000\n3500\n4000\n4500\n5000';

export default function BrowserNpvCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('npv-calculator', '').catch(() => undefined);
  };

  const [initialInvestment, setInitialInvestment] = useState(10000);
  const [discountRate, setDiscountRate] = useState(8);
  const [cashFlowsText, setCashFlowsText] = useState(DEFAULT_CASH_FLOWS);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const flows = cashFlowsText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '')
      .map((line) => Number(line))
      .filter((n) => !Number.isNaN(n));
    const r = discountRate / 100;
    const rows = flows.map((cf, i) => {
      const t = i + 1;
      const discounted = cf / (1 + r) ** t;
      return { t, cf, discounted };
    });
    const totalDiscounted = rows.reduce((sum, row) => sum + row.discounted, 0);
    const npv = totalDiscounted - initialInvestment;
    return { rows, npv };
  }, [cashFlowsText, discountRate, initialInvestment]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Finance</span>
        <h2 className="tool-live-title">NPV calculator</h2>
        <p className="tool-live-subtitle">
          Enter an initial investment, a discount rate, and a cash flow for each future year to get
          the net present value.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Initial investment</span>
            <input
              min={0}
              onChange={(e) => onChange(setInitialInvestment)(Number(e.target.value) || 0)}
              type="number"
              value={initialInvestment}
            />
          </label>
          <label className="tool-live-field">
            <span>Annual discount rate (%)</span>
            <input
              onChange={(e) => onChange(setDiscountRate)(Number(e.target.value) || 0)}
              step={0.1}
              type="number"
              value={discountRate}
            />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Cash flow per year (one per line, year 1 first)</span>
            <textarea
              onChange={(e) => onChange(setCashFlowsText)(e.target.value)}
              rows={6}
              value={cashFlowsText}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong style={{ color: result.npv >= 0 ? '#1baf7a' : '#e34948' }}>
              {result.npv >= 0 ? '+' : ''}
              {result.npv.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </strong>
            <span>
              Net present value ({result.npv >= 0 ? 'worth pursuing' : 'value-destroying'} at this
              rate)
            </span>
          </p>
          {result.rows.map((row) => (
            <p className="tool-live-panel-note" key={row.t} style={{ padding: '0.5rem 0.75rem' }}>
              <span>
                Year {row.t}: {row.cf.toLocaleString()} discounted to{' '}
                {row.discounted.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
            </p>
          ))}
        </section>
      </div>
    </div>
  );
}
